// Notification centre of the advisor workstation (EF-013).
//
// The list is read once at sign in, then kept live by the Supabase realtime
// channel. The unread counter is capped at "9+" so the bell badge keeps its
// 14px geometry whatever the backlog.
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  ErreurHorizon,
  listerNotifications,
  marquerLue,
  marquerLuesEnLot,
  resoudreCiblesNotifications,
  sAbonnerNotifications,
  toErreur,
} from '@horizon/api-client';
import type { CibleNotification } from '@horizon/api-client';
import type { Notification } from '@horizon/types';

/** Above this value the badge shows "9+" instead of the exact count. */
export const PLAFOND_BADGE = 9;

export const useNotificationsStore = defineStore('notifications', () => {
  const liste = ref<readonly Notification[]>([]);
  const chargement = ref(false);
  const erreur = ref<ErreurHorizon | null>(null);
  const abonne = ref(false);
  /** Element designated by a notification, keyed by notification identifier. */
  const cibles = ref<ReadonlyMap<string, CibleNotification>>(new Map());

  let seDesabonner: (() => void) | null = null;
  let destinataireCourant: string | null = null;
  let jeton = 0;

  const nonLues = computed(() => liste.value.filter((notification) => !notification.lu).length);
  /** Badge wording: the exact count up to 9, then "9+". */
  const libelleBadge = computed(() =>
    nonLues.value > PLAFOND_BADGE ? `${PLAFOND_BADGE}+` : String(nonLues.value),
  );
  const estVide = computed(
    () => !chargement.value && erreur.value === null && liste.value.length === 0,
  );
  const erreurReseau = computed<ErreurHorizon | null>(() =>
    erreur.value !== null && erreur.value.reseau ? erreur.value : null,
  );
  const erreurServeur = computed<ErreurHorizon | null>(() =>
    erreur.value !== null && !erreur.value.reseau ? erreur.value : null,
  );

  /** Element a notification opens, or null when its wording designates nothing. */
  function cible(id: string): CibleNotification | null {
    return cibles.value.get(id) ?? null;
  }

  /**
   * Resolve which element each notification designates. The panel stays usable when
   * this secondary read fails, so the failure only drops the opening affordance and
   * never replaces the list by an error state.
   */
  async function resoudreCibles(notifications: readonly Notification[]): Promise<void> {
    try {
      cibles.value = await resoudreCiblesNotifications(notifications);
    } catch {
      cibles.value = new Map();
    }
  }

  /** Load the 50 most recent notifications of the signed in advisor. */
  async function charger(): Promise<void> {
    jeton += 1;
    const courant = jeton;
    chargement.value = true;
    erreur.value = null;
    try {
      const resultat = await listerNotifications();
      if (courant !== jeton) {
        return;
      }
      liste.value = resultat;
    } catch (cause) {
      if (courant !== jeton) {
        return;
      }
      erreur.value = toErreur(cause);
      return;
    } finally {
      if (courant === jeton) {
        chargement.value = false;
      }
    }
    await resoudreCibles(liste.value);
  }

  /** Insert a row pushed by the realtime channel, newest first, without duplicate. */
  function inserer(notification: Notification): void {
    if (liste.value.some((existante) => existante.id === notification.id)) {
      return;
    }
    liste.value = [notification, ...liste.value];
    void resoudreCibles(liste.value);
  }

  /**
   * Subscribe to the realtime channel of an advisor. Calling it again with the
   * same identifier is a no-op; with another one it moves the subscription.
   */
  function sAbonner(destinataireId: string): void {
    if (destinataireCourant === destinataireId && abonne.value) {
      return;
    }
    seDesabonner?.();
    destinataireCourant = destinataireId;
    seDesabonner = sAbonnerNotifications(destinataireId, inserer);
    abonne.value = true;
  }

  function arreterAbonnement(): void {
    seDesabonner?.();
    seDesabonner = null;
    destinataireCourant = null;
    abonne.value = false;
  }

  /**
   * Mark one notification as read. The row is flipped locally first so the badge
   * answers immediately, and rolled back when the server refuses.
   */
  async function marquerCommeLue(id: string): Promise<boolean> {
    const avant = liste.value;
    liste.value = liste.value.map((notification) =>
      notification.id === id ? { ...notification, lu: true } : notification,
    );
    try {
      await marquerLue(id);
      return true;
    } catch (cause) {
      liste.value = avant;
      erreur.value = toErreur(cause);
      return false;
    }
  }

  /**
   * Mark every unread notification as read in one statement. The badge is emptied
   * before the round trip, then realigned on the identifiers the server actually
   * flipped: a row refused by row level security goes back to unread.
   */
  async function toutMarquerCommeLu(): Promise<number> {
    const aTraiter = liste.value.filter((notification) => !notification.lu);
    if (aTraiter.length === 0) {
      return 0;
    }
    const avant = liste.value;
    const identifiants = aTraiter.map((notification) => notification.id);
    liste.value = liste.value.map((notification) =>
      notification.lu ? notification : { ...notification, lu: true },
    );
    try {
      const traitees = await marquerLuesEnLot(identifiants);
      if (traitees.length !== identifiants.length) {
        const acceptees = new Set(traitees);
        liste.value = avant.map((notification) =>
          acceptees.has(notification.id) ? { ...notification, lu: true } : notification,
        );
      }
      return traitees.length;
    } catch (cause) {
      liste.value = avant;
      erreur.value = toErreur(cause);
      return 0;
    }
  }

  /** Replay the last read, wired to the retry action of the network banner. */
  async function reessayer(): Promise<void> {
    await charger();
  }

  function reinitialiser(): void {
    jeton += 1;
    arreterAbonnement();
    liste.value = [];
    cibles.value = new Map();
    chargement.value = false;
    erreur.value = null;
  }

  return {
    liste,
    chargement,
    erreur,
    abonne,
    cibles,
    nonLues,
    libelleBadge,
    estVide,
    erreurReseau,
    erreurServeur,
    cible,
    charger,
    sAbonner,
    arreterAbonnement,
    marquerCommeLue,
    toutMarquerCommeLu,
    reessayer,
    reinitialiser,
  };
});
